'use client'

import { memo, useState, type ComponentType } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

export type MarketingFlipCard = {
  id: string
  frontTitle: string
  frontBody: string
  backTitle: string
  backBody: string
  icon?: ComponentType<{ className?: string }>
}

export type MarketingFlipCardsProps = {
  label?: string
  title: string
  description?: string
  cards: readonly MarketingFlipCard[]
  className?: string
}

const FlipCard = memo(function FlipCard({
  card,
  flipped,
  onFlip,
  onUnflip,
  onToggle,
  reduced,
}: {
  card: MarketingFlipCard
  flipped: boolean
  onFlip: () => void
  onUnflip: () => void
  onToggle: () => void
  reduced: boolean
}) {
  const Icon = card.icon

  return (
    <button
      type="button"
      className={cn('marketing-flip-card', flipped && 'is-flipped')}
      onClick={onToggle}
      onMouseEnter={reduced ? undefined : onFlip}
      onMouseLeave={reduced ? undefined : onUnflip}
      aria-pressed={flipped}
      aria-label={`${card.frontTitle}. ${flipped ? card.backTitle : 'Flip for more'}`}
    >
      <div className="marketing-flip-inner">
        <div className="marketing-flip-face marketing-flip-front">
          {Icon ? (
            <Icon className="mb-3 h-5 w-5 text-[color:var(--ninxl-brand-teal,#21728d)]" />
          ) : null}
          <h3 className="ninxl-8bit-heading ninxl-theme-text text-sm md:text-base">
            {card.frontTitle}
          </h3>
          <p className="ninxl-8bit-body mt-2 text-sm">{card.frontBody}</p>
          <span className="ninxl-8bit-label mt-auto pt-4 tracking-[0.14em] text-[color:var(--ninxl-brand-teal)]">
            FLIP
          </span>
        </div>
        <div className="marketing-flip-face marketing-flip-back">
          <h3 className="ninxl-8bit-heading text-sm text-[color:var(--ninxl-brand-teal)] md:text-base">
            {card.backTitle}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[color:var(--ninxl-theme-fg)]/90">
            {card.backBody}
          </p>
        </div>
      </div>
    </button>
  )
})

/**
 * baby: Webflow flip-wrapper cards: front/back on hover or click
 * (landing/01, web3, home).
 */
export const MarketingFlipCards = memo(function MarketingFlipCards({
  label = 'DETAILS',
  title,
  description,
  cards,
  className,
}: MarketingFlipCardsProps) {
  const reduced = Boolean(useReducedMotion())
  const [activeId, setActiveId] = useState<string | null>(null)

  if (cards.length === 0) {
    return null
  }

  return (
    <section className={cn('mx-auto max-w-6xl px-6', className)} data-section="flip-cards">
      <div className="mb-8 text-center md:mb-10">
        <div className="ninxl-8bit-label mb-2 tracking-[0.2em] text-[color:var(--ninxl-brand-teal)]">
          {label}
        </div>
        <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-base md:text-lg">{title}</h2>
        {description ? (
          <p className="ninxl-8bit-body mx-auto max-w-2xl">{description}</p>
        ) : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <FlipCard
            key={card.id}
            card={card}
            flipped={activeId === card.id}
            onFlip={() => setActiveId(card.id)}
            onUnflip={() => setActiveId((prev) => (prev === card.id ? null : prev))}
            onToggle={() =>
              setActiveId((prev) => (prev === card.id ? null : card.id))
            }
            reduced={reduced}
          />
        ))}
      </div>
    </section>
  )
})
